/**
 * Puts the cart's gift lines back to what the cart has earned.
 *
 * We add the gift, so we own taking it back. Without this, deleting the product leaves the $0 gift
 * stranded on its own — the cart state that let ~90 orders check out with no product in them.
 *
 * Loaded on every page rather than beside the product form, because the removal that strands a gift
 * happens in the cart drawer, which is everywhere.
 */

import { CartUpdateEvent } from "@theme/events";
import {
  cartWasNonEmptyAtRender,
  resolveGiftLineCorrections,
  resolveMissingGiftLine,
} from "@theme/free-gift";

/** Ajax Cart endpoints that can change what's in the cart. `/cart.js` only reads, so it is absent. */
const CART_MUTATION_PATH = /\/cart\/(add|change|update|clear)(\.js)?$/;

/**
 * `/cart/add.js` answers with the lines it added rather than with the whole cart, so its response
 * can't stand in for one. Every other mutation returns the full cart and saves us a round trip.
 */
const PARTIAL_CART_RESPONSE_PATH = /\/cart\/add(\.js)?$/;

/**
 * The cart to reconcile against, preferring the copy the triggering mutation already returned.
 *
 * Reusing it removes a round trip from the window in which the customer is looking at a cart we
 * haven't corrected yet, and narrows the gap between reading the cart and writing to it.
 *
 * @param {Response | null} mutationResponse - A clone, so reading it here doesn't disturb the caller.
 * @returns {Promise<{items?: Array<any>}>}
 */
async function readCart(mutationResponse) {
  if (mutationResponse) {
    try {
      const cart = await mutationResponse.json();
      if (Array.isArray(cart?.items)) return cart;
    } catch {
      // Not every cart path answers with JSON — the non-Ajax `POST /cart/change` form redirects to
      // HTML. Fall through and ask for the cart directly rather than skipping the reconcile.
    }
  }

  return fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
}

/**
 * Deliberately unguarded against re-entry, even though its own write is a cart mutation that
 * re-triggers the watcher below. The second pass finds a cart that already agrees and stops, so this
 * settles after one extra read. An in-flight latch would be the fragile choice — one request that
 * never settled would leave it latched and silently disable the whole thing.
 *
 * ⚠️ Still a read followed by a write, with no compare-and-set: the Ajax Cart API offers none. Both
 * directions of that race are reachable. A qualifying add landing mid-pass can have its fresh gift
 * written back to 0. A removal landing mid-pass leaves the restore below adding a gift to a cart
 * that no longer earns one — the $0 shape this whole feature exists to prevent.
 *
 * Neither is durable: every write here is itself a cart mutation, so it triggers a pass that reads
 * the cart it left behind and corrects it. This converges once the customer stops changing the cart.
 *
 * Converging is not the same as never occurring, and nothing on this page holds checkout shut while
 * it converges — a second tab isn't even listening. A cart holding only the gift must therefore be
 * refused server-side at checkout; no amount of care in this file can be the guarantee.
 *
 * @param {Response | null} [mutationResponse]
 */
async function reconcileGiftLines(mutationResponse = null) {
  const cart = await readCart(mutationResponse);
  const items = cart.items ?? [];

  const corrections = resolveGiftLineCorrections(items);
  const missingGiftLine = corrections.length ? null : resolveMissingGiftLine(items);

  if (!corrections.length && !missingGiftLine) return;

  const response = missingGiftLine
    ? await fetch(Theme.routes.cart_add_url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [missingGiftLine] }),
      })
    : await fetch(`${Theme.routes.cart_update_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updates: Object.fromEntries(
            corrections.map(({ key, quantity }) => [key, quantity])
          ),
        }),
      });

  // A rejected write leaves the cart exactly as wrong as it was found, and the wrapper below only
  // reconciles successful responses, so nothing retries. Left unchecked this is invisible: the
  // restore path never reads its own response body, and the correction path would take an error
  // payload for a cart and announce `item_count` 0 to the cart icon.
  if (!response.ok) {
    throw new Error(
      `Shopify rejected the gift-line ${missingGiftLine ? "restore" : "correction"}: ${await describeCartRejection(response)}`
    );
  }

  // `/cart/add.js` answers with the lines it added, not the cart, so the count has to be read back.
  const correctedCart = missingGiftLine
    ? await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json())
    : await response.json();

  // Every cart surface in the theme paints from HTML the server rendered inside the response to the
  // customer's own mutation — captured before this correction existed. Left alone, the cart page
  // goes on showing the stranded gift until something else redraws it. cart-items-component answers
  // this event by re-rendering its section from the server, and cart-icon takes the count as given.
  document.dispatchEvent(
    new CartUpdateEvent({}, "free-gift-reconciler", {
      itemCount: correctedCart.item_count ?? 0,
      source: "free-gift-reconciler",
    })
  );

  window.upcartRefreshCart?.();
}

/**
 * @param {RequestInfo | URL} input
 * @returns {string | null} The mutated cart path, or null when this request isn't one.
 */
function cartMutationPath(input) {
  const url =
    typeof input === "string"
      ? input
      : input instanceof Request
        ? input.url
        : String(input);

  const { pathname } = new URL(url, location.origin);

  return CART_MUTATION_PATH.test(pathname) ? pathname : null;
}

/**
 * Watching the network rather than UpCart's callbacks.
 *
 * UpCart publishes upcartOnItemRemoved / upcartOnCartUpdated / upcartOnCartLoaded for exactly this
 * purpose, but measured on the storefront each of them skips removals, including all three together.
 * Every cart change does go out over the Ajax Cart API, so the request is the one signal that can't
 * be missed — whoever made it, drawer or theme or another app.
 *
 * Wrapping late is safe: UpCart reads window.fetch at call time rather than capturing it at load.
 */
const nativeFetch = window.fetch;

window.fetch = async function (input, init) {
  const response = await nativeFetch.call(this, input, init);

  // Everything from here is ours, and none of it is the caller's problem: a throw would reject a
  // request that already succeeded, for every script on the page.
  try {
    const path = response.ok ? cartMutationPath(input) : null;

    if (path) {
      // clone() only tees the stream, so the body is read on our side of the un-awaited call below
      // rather than on the caller's. Holding the caller's response until our reconcile finishes is
      // precisely what made the app we replaced dangerous: it stalled the add long enough for a
      // customer to check out mid-flight.
      const cartSnapshot = PARTIAL_CART_RESPONSE_PATH.test(path)
        ? null
        : response.clone();

      void reconcileGiftLines(cartSnapshot).catch(reportReconcileFailure);
    }
  } catch (error) {
    reportReconcileFailure(error);
  }

  return response;
};

/**
 * Shopify's own account of why it turned a cart write down, for the message above.
 *
 * Cart errors answer with a JSON body carrying `description`, but a rejection from in front of the
 * app — rate limiting, a challenge page — answers with HTML, so the parse is allowed to fail.
 *
 * @param {Response} response
 * @returns {Promise<string>}
 */
async function describeCartRejection(response) {
  try {
    const body = await response.json();
    return body?.description ?? body?.message ?? `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

/**
 * A failed reconcile leaves the stranded gift in place until some later page view picks it up, which
 * is invisible from the outside — so say so loudly enough to reach session recordings.
 *
 * @param {unknown} error
 */
function reportReconcileFailure(error) {
  console.error("[free-gift] Could not reconcile the cart's gift lines.", error);
}

// Catches a cart that arrives already wrong — stranded from another tab or an abandoned session,
// missing a gift a failed add never attached, or changed by the non-Ajax `POST /cart` form, which
// navigates away rather than passing through the wrapper above.
if (cartWasNonEmptyAtRender()) {
  reconcileGiftLines().catch(reportReconcileFailure);
}
